import React, { useContext, useEffect, useRef, useState } from 'react';
import TasksTabView from '../view/TasksTabView';
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow';
import DragHandleIcon from '@material-ui/icons/DragHandle';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import CircleIcon from '@material-ui/icons/FiberManualRecord';
import styled from 'styled-components/macro';
import fetchAPI from '../../../utils/fetchAPI';
import { FetchCallbacksContext } from '../../../internal-components/FetchCallbacks';
import useGlobalVariables from '../../../hooks/useGlobalVariables';
import { isViewOnlyAdminOfForm } from '../../../utils/formUtils';
import getUrlParam from '../../../utils/getUrlParam';

const HighPriorityIcon = styled(DoubleArrowIcon)`
  color: #df8e82;
  transform: rotate(270deg);
  vertical-align: middle;
`;

const MediumPriorityIcon = styled(DragHandleIcon)`
  color: #fdb259;
  vertical-align: middle;
`;

const LowPriorityIcon = styled(KeyboardArrowDownIcon)`
  color: #80b792;
  vertical-align: middle;
`;

export const priorityOptions = [
  {
    value: 'high',
    'data-testid': `task-priority-high`,
    text: (
      <>
        <HighPriorityIcon /> High
      </>
    )
  },
  {
    value: 'medium',
    'data-testid': `task-priority-medium`,
    text: (
      <>
        <MediumPriorityIcon /> Medium
      </>
    )
  },
  {
    value: 'low',
    'data-testid': `task-priority-low`,
    text: (
      <>
        <LowPriorityIcon /> Low
      </>
    )
  }
];

const AssignedStatusIcon = styled(CircleIcon)`
  color: #4c84ff;
  vertical-align: middle;
`;

const InProgressStatusIcon = styled(CircleIcon)`
  color: #fdb259;
  vertical-align: middle;
`;

const CompleteStatusIcon = styled(CircleIcon)`
  color: #80b792;
  vertical-align: middle;
`;

const PriorityDiv = styled.div`
  display: inline-block;
  width: 100px;
`;

export const statusOptions = [
  {
    value: 'assigned',
    'data-testid': `task-status-assigned`,
    text: (
      <>
        <AssignedStatusIcon /> Assigned
      </>
    )
  },
  {
    value: 'inProgress',
    'data-testid': `task-status-inProgress`,
    text: (
      <>
        <InProgressStatusIcon /> In progress
      </>
    )
  },
  {
    value: 'complete',
    'data-testid': `task-status-complete`,
    text: (
      <>
        <CompleteStatusIcon /> Complete
      </>
    )
  }
];

export const statusToDisplayTextMap = {
  assigned: 'Assigned',
  complete: 'Complete',
  inProgress: 'In Progress'
};

export const priorityToDisplayMap = {
  high: (
    <PriorityDiv>
      <HighPriorityIcon /> High
    </PriorityDiv>
  ),
  medium: (
    <PriorityDiv>
      <MediumPriorityIcon />
      Medium
    </PriorityDiv>
  ),
  low: (
    <PriorityDiv>
      <LowPriorityIcon />
      Low
    </PriorityDiv>
  )
};

const INITIAL_SORT = { id: 'dueDate', value: 'asc' };

const TasksTabEngine = ({ formId, initialTask, submissionId }) => {
  const showedInitialTask = useRef(false);
  const { fullJWT, setIsLoading } = useGlobalVariables();

  const { openSnackbar } = useContext(FetchCallbacksContext);
  const [isAddTaskDialogOpen, setIsAddTaskDialogOpen] = useState(false);
  const [isEditTaskDialogOpen, setIsEditTaskDialogOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [viewOnly, setViewOnly] = useState(false);
  const [sortObject, setSortObject] = useState(INITIAL_SORT);
  const [areTasksLoaded, setAreTasksLoaded] = useState(false);
  const [refreshSeed, setRefreshSeed] = useState(1);
  const [taskBeingEdited, setTaskBeingEdited] = useState(null);
  const clickedAdd = () => {
    setIsAddTaskDialogOpen(true);
  };
  const addFilePromise = (taskId, file) => {
    const id = encodeURIComponent(file.name);
    file.id = id;
    const formData = new FormData();
    formData.append('file', file, id);
    return fetchAPI(`/tasks/v1/eu/tasks/${taskId}/upload`, {
      method: 'PUT',
      body: formData
    });
  };
  const closeAddTaskDialog = newTask => {
    setIsAddTaskDialogOpen(false);
    if (newTask) {
      fetchAPI(`/tasks/v1/tasks`, {
        method: 'POST',
        body: {
          assigneeId: newTask.assigneeId,
          parentEntityId: submissionId,
          parentAssetId: formId,
          title: newTask.title,
          description: newTask.description,
          priority: newTask.priority,
          status: newTask.status,
          dueDate: newTask.dueDate
        },
        handleResponse: r => {
          return r.headers.map.location;
        }
      })
        .then(newTaskId => {
          let onePromise,
            allPromises = [];
          if (newTask.existingFiles.length) {
            newTask.existingFiles.forEach(one => {
              onePromise = addFilePromise(newTaskId, one);
              allPromises.push(onePromise);
            });
            Promise.all(allPromises).then(res => {
              loadTasks();
              openSnackbar('success', 'snackbars.tasks.creation_success');
            });
          } else {
            loadTasks();
            openSnackbar('success', 'snackbars.tasks.creation_success');
          }
        })
        .catch(() => {
          loadTasks();
          openSnackbar('warning', 'snackbars.tasks.creation_failure');
        });
    }
  };
  const closeEditTaskDialog = () => {
    setIsEditTaskDialogOpen(false);
    loadTasks();
  };
  const clickedTask = task => {
    setTaskBeingEdited(task);
    setIsEditTaskDialogOpen(true);
  };
  const editedTaskCallback = (task, assignedUserChanged) => {
    if (assignedUserChanged) {
      setIsEditTaskDialogOpen(false);
      setTaskBeingEdited(null);
      loadTasks(task.id);
      setIsLoading(false);
    } else {
      setTaskBeingEdited(task);
      setRefreshSeed(+new Date());
    }
  };
  const getUserInfoByIdsPromise = ids => {
    const VINCI_SUPPORT_ID = 'f2618836-2355-496c-b245-1c1ae2258101';
    return new Promise((resolve, reject) => {
      if (!ids.length) {
        resolve({});
      }
      const idClause = ids.map(oneId => ({
        field: "'id'",
        op: '=',
        value: oneId,
        internOp: 'OR'
      }));
      fetchAPI(
        `/users/v1/users?offset=0&limit=100&sort='status' asc&query=[${JSON.stringify(
          idClause
        )}]`
      ).then(response => {
        const userResults = response.results || [];
        let resultsById = {};
        userResults.forEach(one => {
          resultsById[one.id] = one;
        });
        if (ids.includes(VINCI_SUPPORT_ID) && !resultsById[VINCI_SUPPORT_ID]) {
          resultsById[VINCI_SUPPORT_ID] = {
            id: VINCI_SUPPORT_ID,
            info: {
              email: 'vinci_support@vinciworks.com',
              firstName: 'Vinci',
              lastName: 'Support'
            }
          };
        }
        resolve(resultsById);
      });
    });
  };
  useEffect(() => {
    const taskId = initialTask || null;
    if (!showedInitialTask.current) {
      showedInitialTask.current = true;
      loadTasks(taskId);
    } else {
      loadTasks();
    }
  }, []);

  const loadTasks = openTaskId => {
    let userIdsToLookup = [];
    if (formId && submissionId) {
      fetchAPI(
        `/forms/v1/forms/${formId}/submissions/${submissionId}/tasks`
      ).then(tasksRes => {
        if (tasksRes && tasksRes.length) {
          userIdsToLookup = [
            ...tasksRes.map(one => one.assigneeId),
            ...tasksRes.map(one => one.metadata?.createdByUserId)
          ];
          userIdsToLookup = [...new Set(userIdsToLookup)];
          getUserInfoByIdsPromise(userIdsToLookup).then(usersRes => {
            const combinedResults = [];
            tasksRes.forEach(one => {
              let oneCombinedRes = { ...one };
              const assigneeUserMatch = usersRes[one.assigneeId];
              if (assigneeUserMatch) {
                oneCombinedRes.assigneeName =
                  assigneeUserMatch.info.firstName +
                    ' ' +
                    assigneeUserMatch.info.lastName || '';
                oneCombinedRes.assigneeEmailAddress =
                  assigneeUserMatch.info.email || '';
              } else {
                oneCombinedRes.assigneeName = '';
                oneCombinedRes.assigneeEmailAddress = '';
              }
              const authorUserMatch = usersRes[one.metadata.createdByUserId];
              if (authorUserMatch) {
                oneCombinedRes.authorName =
                  authorUserMatch.info.firstName +
                    ' ' +
                    authorUserMatch.info.lastName || '';
                oneCombinedRes.authorEmailAddress =
                  authorUserMatch.info.email || '';
              } else {
                oneCombinedRes.authorName = '';
                oneCombinedRes.authorEmailAddress = '';
              }
              combinedResults.push(oneCombinedRes);
            });
            if (isViewOnlyAdminOfForm(formId, fullJWT)) {
              setViewOnly(true);
            }
            setTasks(combinedResults);
            if (openTaskId) {
              let matchingTask = combinedResults.find(
                one => one.id === openTaskId
              );
              if (matchingTask) {
                setTaskBeingEdited(matchingTask);
                setIsEditTaskDialogOpen(true);
              }
            }
          });
        } else {
          if (isViewOnlyAdminOfForm(formId, fullJWT)) {
            setViewOnly(true);
          }
          setTasks([]);
          setAreTasksLoaded(true);
        }
      });
    }
  };

  const getSortedRows = (rowData, colId, newSortValue) => {
    let newSortedRows = [...rowData];
    const descFactor = newSortValue === 'desc' ? -1 : 1;
    if (colId === 'formattedPriority') {
      newSortedRows.sort((a, b) => {
        const possibleValues = ['low', 'medium', 'high'];
        const valA = possibleValues.indexOf(a.priority);
        const valB = possibleValues.indexOf(b.priority);
        if (valA < valB) {
          return -descFactor;
        }
        if (valA > valB) {
          return descFactor;
        }
        return 0;
      });
    } else {
      const comparisonColumnId =
        colId === 'formattedDueDate'
          ? 'dueDate'
          : colId === 'authorDisplay'
          ? 'authorName'
          : colId === 'assigneeDisplay'
          ? 'assigneeName'
          : colId;
      newSortedRows.sort((a, b) => {
        const valA = ('' + a[comparisonColumnId]).toUpperCase();
        const valB = ('' + b[comparisonColumnId]).toUpperCase();
        if (valA < valB) {
          return -descFactor;
        }
        if (valA > valB) {
          return descFactor;
        }

        // names must be equal
        return 0;
      });
    }
    return newSortedRows;
  };

  const [sortedRows, setSortedRows] = useState(
    getSortedRows(tasks, INITIAL_SORT.id, INITIAL_SORT.value)
  );

  useEffect(() => {
    setSortedRows(getSortedRows(tasks, sortObject.id, sortObject.value));
    if (tasks.length) {
      setAreTasksLoaded(true);
    }
  }, [tasks]);

  const onSortChange = (colId, newSortValue) => {
    const newSortedRows = getSortedRows(tasks, colId, newSortValue);
    setSortObject({ id: colId, value: newSortValue });
    setSortedRows(newSortedRows);
  };

  const viewProps = {
    areTasksLoaded,
    clickedAdd,
    clickedTask,
    closeAddTaskDialog,
    closeEditTaskDialog,
    editedTaskCallback,
    isAddTaskDialogOpen,
    isEditTaskDialogOpen,
    refreshSeed,
    sortObject,
    tasks: sortedRows,
    taskBeingEdited,
    onSortChange,
    viewOnly
  };

  return <TasksTabView {...viewProps} />;
};

export default TasksTabEngine;
